import { API_URL } from './config';
import { currentToken } from './auth';

/** Typed client for report templates (`/query/templates`), tenant-scoped by the token. */

export interface TemplateReport {
  id: string;
  title: string;
  question: string;
  /** Widget override; absent ⇒ auto-classify by result. */
  widget?: string;
  /** Saved dashboard-tile width on the 12-col grid (3/4/6/12); absent ⇒ auto-size. */
  span?: number;
}
export interface ReportTemplate {
  id: string;
  name: string;
  reports: TemplateReport[];
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface TemplateSummary {
  id: string;
  name: string;
  reportCount: number;
  /** The tenant's landing dashboard — at most one row carries this. */
  isDefault: boolean;
  updatedAt: string;
}

/** A report the builder is composing (id optional until saved). */
export interface DraftReport {
  id?: string;
  title: string;
  question: string;
  widget?: string;
  /** Saved dashboard-tile width on the 12-col grid (3/4/6/12); absent ⇒ auto-size. */
  span?: number;
}

/**
 * A question routed from Ask into the templates tab. Without `templateId` it starts
 * a brand-new template seeded with this one report; with it, that existing template
 * opens in edit mode (its items loaded) and this report is appended.
 */
export interface AddToReportSeed {
  report: DraftReport;
  templateId?: string;
}

function headers(json = false): Record<string, string> {
  const token = currentToken();
  return {
    ...(json ? { 'content-type': 'application/json' } : {}),
    ...(token ? { authorization: `Bearer ${token}` } : {}),
  };
}

/** The tenant's landing dashboard, or null when none is marked. */
export async function getDefaultTemplate(): Promise<ReportTemplate | null> {
  const res = await fetch(`${API_URL}/query/templates/default`, { headers: headers() });
  if (!res.ok) return null;
  return ((await res.json()) as { template: ReportTemplate | null }).template;
}

/** Make one template the tenant's landing dashboard, demoting whichever held it. */
export async function markTemplateDefault(id: string): Promise<boolean> {
  const res = await fetch(`${API_URL}/query/templates/${id}/default`, { method: 'POST', headers: headers() });
  return res.ok;
}

export async function listTemplates(): Promise<TemplateSummary[]> {
  const res = await fetch(`${API_URL}/query/templates`, { headers: headers() });
  if (!res.ok) return [];
  return (await res.json()) as TemplateSummary[];
}

export async function getTemplate(id: string): Promise<ReportTemplate | null> {
  const res = await fetch(`${API_URL}/query/templates/${id}`, { headers: headers() });
  if (!res.ok) return null;
  return (await res.json()) as ReportTemplate;
}

export async function createTemplate(name: string, reports: DraftReport[]): Promise<ReportTemplate> {
  const res = await fetch(`${API_URL}/query/templates`, { method: 'POST', headers: headers(true), body: JSON.stringify({ name, reports }) });
  if (!res.ok) throw new Error(await errMsg(res));
  return (await res.json()) as ReportTemplate;
}

export async function updateTemplate(id: string, name: string, reports: DraftReport[]): Promise<ReportTemplate> {
  const res = await fetch(`${API_URL}/query/templates/${id}`, { method: 'PUT', headers: headers(true), body: JSON.stringify({ name, reports }) });
  if (!res.ok) throw new Error(await errMsg(res));
  return (await res.json()) as ReportTemplate;
}

export async function deleteTemplate(id: string): Promise<void> {
  const res = await fetch(`${API_URL}/query/templates/${id}`, { method: 'DELETE', headers: headers() });
  if (!res.ok) throw new Error(await errMsg(res));
}

async function errMsg(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  return body.message ?? `${res.status} ${res.statusText}`;
}
